import { useState, useEffect, useCallback } from 'react';
import { useDevice } from '@/hooks';
import { eventService } from '@/api/services';
import type { NAEventObject } from '@/api/models';
import { Button } from '@/components/common/Button/Button';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { formatDateTime } from '@/lib/formatters';
import { DEFAULT_EVENT_LOG_DAYS, DEFAULT_EVENT_LOG_ENTRIES } from '@/config/constants';
import { eventTypeColor } from '@/lib/enum-resolvers';

const DAY_OPTIONS = [1, 3, 7, 14, 30];
const ENTRY_OPTIONS = [20, 50, 100];

function getMessageTypeBgClass(messageType: unknown): string {
  const color = eventTypeColor(messageType);
  switch (color) {
    case 'red': return 'bg-red-50 dark:bg-red-900/10';
    case 'yellow': return 'bg-yellow-50 dark:bg-yellow-900/10';
    case 'blue': return 'bg-blue-50 dark:bg-blue-900/10';
    default: return '';
  }
}

export function EventLogPage() {
  const { selectedDevice, deviceInfoLoading } = useDevice();
  const deviceUUID = selectedDevice?.deviceProperties.deviceUUID ?? '';

  const [events, setEvents] = useState<NAEventObject[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maxDays, setMaxDays] = useState(DEFAULT_EVENT_LOG_DAYS);
  const [maxEntries, setMaxEntries] = useState(DEFAULT_EVENT_LOG_ENTRIES);

  const fetchEvents = useCallback(async () => {
    if (!deviceUUID) return;
    setLoading(true);
    setError('');
    try {
      const resp = await eventService.getEventLog({
        deviceUUID,
        maxNumDays: maxDays,
        maxNumEntries: maxEntries,
      });
      setEvents(resp.eventList ?? []);
    } catch {
      setError('Failed to load event log.');
    } finally {
      setLoading(false);
    }
  }, [deviceUUID, maxDays, maxEntries]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (deviceInfoLoading && !events.length) return <PageSpinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Event Log</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            View system events and alerts
            {events.length > 0 && (
              <span className="ml-2 font-medium text-slate-700 dark:text-slate-300">
                ({events.length} events)
              </span>
            )}
          </p>
        </div>
        <Button variant="secondary" onClick={fetchEvents} loading={loading}>
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">Days:</span>
          <div className="flex rounded-lg border border-slate-200 dark:border-navy-700">
            {DAY_OPTIONS.map((d) => (
              <button
                key={d}
                onClick={() => setMaxDays(d)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
                  maxDays === d
                    ? 'bg-teal-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-navy-900 dark:text-slate-400 dark:hover:bg-navy-800'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600 dark:text-slate-400">Max:</span>
          <div className="flex rounded-lg border border-slate-200 dark:border-navy-700">
            {ENTRY_OPTIONS.map((n) => (
              <button
                key={n}
                onClick={() => setMaxEntries(n)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-lg last:rounded-r-lg ${
                  maxEntries === n
                    ? 'bg-teal-500 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50 dark:bg-navy-900 dark:text-slate-400 dark:hover:bg-navy-800'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Event Table */}
      {loading ? (
        <PageSpinner />
      ) : events.length === 0 ? (
        <EmptyState
          title="No events found"
          description="No events were recorded in the selected time period."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 dark:border-navy-700">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 dark:border-navy-700 dark:bg-navy-800">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white dark:divide-navy-800 dark:bg-navy-900">
                {events.map((event, idx) => (
                  <tr
                    key={event.eventUUID ?? idx}
                    className={`transition-colors hover:bg-slate-50 dark:hover:bg-navy-800 ${getMessageTypeBgClass(event.messageType)}`}
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {formatDateTime(event.createdDT)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">
                      {event.messageStr}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
